/**
 * Add or change a calendar event (a live session, the batch-record day, a
 * meeting). Events are things that happen at a time; tasks are things to do.
 *
 * Editing works from what the calendar knows about the event (title, time,
 * length, link). Its repeat and start day are only sent when you change them,
 * so editing one week's copy never moves the whole series by accident.
 */
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, Input, Label, Modal, ModalFooter, Select, Textarea, useConfirm, useToast } from "@/components/ui";
import { ChoicePills, DayToggles, PersonPills, TimeInput } from "../fields";
import { weekday } from "../lib";
import { DayPicker } from "../DayPicker";
import type { CalItem, Person } from "../types";

type RepeatMode = "keep" | "none" | "week" | "day" | "month";

export interface EventTarget {
  /** Present when editing: the calendar item that was clicked. */
  item?: CalItem;
  /** The day to start a new one on. */
  day?: string;
}

const LENGTHS = [15, 30, 45, 60, 90, 120, 180, 240, 480];
const lengthLabel = (m: number) => (m < 60 ? `${m} min` : m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m / 60} h`);

export function EventModal({
  target, onClose, onSaved, people, meId,
}: { target: EventTarget | null; onClose: () => void; onSaved: () => void; people: Person[]; meId: string | null }) {
  return (
    <Modal open={!!target} onClose={onClose} title={target?.item ? "Event" : "Add an event"} description={target?.item ? undefined : "Something that happens at a time: a live session, a meeting, a record day."} size="md">
      {target && <EventForm key={target.item?.id ?? target.day ?? "new"} target={target} onClose={onClose} onSaved={onSaved} people={people} meId={meId} />}
    </Modal>
  );
}

function EventForm({ target, onClose, onSaved, people, meId }: { target: EventTarget; onClose: () => void; onSaved: () => void; people: Person[]; meId: string | null }) {
  const toast = useToast();
  const confirm = useConfirm();
  const it = target.item;
  const editing = !!it;
  const eventId = it ? (it.event_id ?? it.id.split(":")[0]!) : null;
  const startDay = it?.date ?? target.day ?? "";
  // The stored event (when the calendar sent it) so the form starts from the truth.
  const known = editing && it!.event_repeat !== undefined;
  const rule = it?.event_repeat ?? null;
  const initialRepeat: RepeatMode = !editing ? "none" : !known ? "keep" : rule ? rule.period : "none";
  const initialDays = rule?.days?.length ? rule.days : startDay ? [weekday(startDay)] : [];
  const initialUntil = it?.until ?? "";
  const initialNotes = it?.notes ?? "";
  const initialOwner = editing ? (it!.owner_id ?? null) : people.some((p) => p.id === meId) ? meId : null;
  const [title, setTitle] = useState(it?.title ?? "");
  const [date, setDate] = useState(startDay);
  const [time, setTime] = useState(it?.start ?? "");
  const [minutes, setMinutes] = useState(it?.minutes ?? 60);
  const [repeat, setRepeat] = useState<RepeatMode>(initialRepeat);
  const [days, setDays] = useState<number[]>(initialDays);
  const [every, setEvery] = useState(rule?.every ?? 1);
  const [until, setUntil] = useState(initialUntil);
  const [url, setUrl] = useState(it?.link ?? "");
  const [notes, setNotes] = useState(initialNotes);
  const [owner, setOwner] = useState<string | null>(initialOwner);
  const [busy, setBusy] = useState(false);

  const repeatBody = () => {
    if (repeat === "none") return null;
    if (repeat === "week") return { period: "week" as const, every, days: days.length ? days : [weekday(date)] };
    if (repeat === "day") return { period: "day" as const, every };
    return { period: "month" as const, every };
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Give the event a name"); return; }
    if (!date) { toast.error("Pick the day it happens"); return; }
    setBusy(true);
    try {
      if (editing && eventId) {
        const body: Record<string, unknown> = {};
        if (title.trim() !== it!.title) body.title = title.trim();
        if ((time || null) !== (it!.start ?? null)) body.start_time = time || null;
        if (minutes !== (it!.minutes ?? 60)) body.duration_min = minutes;
        if ((url.trim() || null) !== (it!.link ?? null)) body.url = url.trim() || null;
        if (date !== it!.date) body.starts_on = date;
        const repeatChanged = repeat !== initialRepeat || every !== (rule?.every ?? 1)
          || (repeat === "week" && [...days].sort().join() !== [...initialDays].sort().join())
          || until !== initialUntil;
        if (repeat !== "keep" && repeatChanged) { body.repeat = repeatBody(); body.until = repeat === "none" ? null : until || null; }
        if (notes.trim() !== initialNotes.trim()) body.notes = notes.trim() || null;
        if (owner !== initialOwner) body.owner_id = owner;
        if (Object.keys(body).length) await api.patch(`/command/events/${eventId}`, body);
        toast.success("Event saved");
      } else {
        await api.post("/command/events", {
          title: title.trim(), starts_on: date, start_time: time || null, duration_min: minutes,
          repeat: repeatBody(), until: repeat === "none" ? null : until || null,
          url: url.trim() || null, notes: notes.trim() || null, owner_id: owner,
        });
        toast.success("Event added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the event");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!eventId) return;
    const ok = await confirm({ title: "Delete this event?", description: "If it repeats, every repeat of it goes too.", confirmLabel: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/command/events/${eventId}`);
      toast.success("Event deleted");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete the event");
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <div className="-mx-5 max-h-[calc(100dvh-13rem)] space-y-4 overflow-y-auto px-5 pb-1">
      <div>
        <Label htmlFor="ev-title">What is it?</Label>
        <Input id="ev-title" autoFocus={!editing} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Live Q&A, batch record day, call with Jay" />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label>Day</Label>
          <DayPicker value={date} onChange={setDate} clearable={false} />
        </div>
        <div>
          <Label>Starts at</Label>
          <TimeInput value={time} onChange={setTime} className="w-full" />
        </div>
        <div>
          <Label>How long</Label>
          <Select value={String(minutes)} onChange={(e) => setMinutes(Number(e.target.value))}>
            {[...new Set([...LENGTHS, minutes])].sort((a, b) => a - b).map((m) => <option key={m} value={m}>{lengthLabel(m)}</option>)}
          </Select>
        </div>
      </div>
      {!time && <p className="-mt-2 text-[11px] text-zinc-500">No start time means it shows at the top of the day.</p>}

      <div className="space-y-2">
        <Label>Repeat</Label>
        <ChoicePills<RepeatMode>
          value={repeat}
          onChange={(m) => setRepeat(m ?? "none")}
          options={[
            ...(editing ? [{ value: "keep" as RepeatMode, label: "Keep as it is" }] : []),
            { value: "none", label: "Just once" },
            { value: "week", label: "Weekly" },
            { value: "day", label: "Daily" },
            { value: "month", label: "Monthly" },
          ]}
        />
        {repeat === "week" && (
          <div className="space-y-2 rounded-xl border border-zinc-800/80 bg-zinc-900/40 p-3">
            <DayToggles value={days} onChange={setDays} />
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              Every
              <Select value={String(every)} onChange={(e) => setEvery(Number(e.target.value))} className="h-8 w-20">
                {[1, 2, 3, 4].map((x) => <option key={x} value={x}>{x}</option>)}
              </Select>
              {every === 1 ? "week" : "weeks"}
            </label>
          </div>
        )}
        {(repeat === "day" || repeat === "month") && (
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            Every
            <Select value={String(every)} onChange={(e) => setEvery(Number(e.target.value))} className="h-8 w-20">
              {[1, 2, 3, 4, 6].map((x) => <option key={x} value={x}>{x}</option>)}
            </Select>
            {repeat === "day" ? (every === 1 ? "day" : "days") : every === 1 ? "month" : "months"}
          </label>
        )}
        {repeat !== "none" && repeat !== "keep" && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400">
            Until
            <DayPicker value={until} onChange={setUntil} placeholder="No end" className="w-44" />
          </div>
        )}
        {editing && repeat === "keep" && date !== it!.date && (
          <p className="text-[11px] text-amber-300/80">If this event repeats, changing the day moves where the repeats start from.</p>
        )}
      </div>

      <div>
        <Label htmlFor="ev-url">Link</Label>
        <Input id="ev-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Zoom, Meet or page link (optional)" />
      </div>
      {(!editing || known) && (
        <>
          <div>
            <Label>Whose is it</Label>
            <PersonPills people={people} value={owner} onChange={setOwner} />
          </div>
          <div>
            <Label htmlFor="ev-notes">Notes</Label>
            <Textarea id="ev-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className="min-h-[3.5rem]" />
          </div>
        </>
      )}

      </div>

      <ModalFooter>
        {editing && (
          <Button type="button" variant="ghost" size="sm" onClick={remove} leftIcon={<Trash2 className="h-3.5 w-3.5" />} className="mr-auto text-rose-300 hover:bg-rose-500/10 hover:text-rose-200">
            Delete
          </Button>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button type="submit" size="sm" loading={busy}>{editing ? "Save" : "Add event"}</Button>
      </ModalFooter>
    </form>
  );
}
